import { Box, Row, Tooltip, TextWithTooltip } from "@hightouch/htui";
import {
  type FC,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ErrorBar } from "src/components/analytics/breakdowns/error-bar";
import { ChartTooltip } from "src/components/charts/chart-tooltip";
import { chartBarRadius } from "src/components/charts/chart-utils";

type ErrorBarProps = {
  lowerBound: number;
  upperBound: number;
  value: number; // numbered value of the bar, not the string
  color?: string;
};

type AnalyticsBarTooltip =
  | {
      type: "default";
      defaultTooltipProps: {
        tooltipValue: string;
        tooltipText?: string;
      };
    }
  | {
      type: "custom";
      customTooltip: ReactNode;
    };

type AnalyticsBarProps = {
  color: string;
  metricName: string;
  value: string;
  width: number;
  tooltip: AnalyticsBarTooltip | undefined;
  /** The variant of the value text @default "monospace" */
  valueVariant?: "monospace" | "default";
  errorBar?: ErrorBarProps;
  /**
   * Whole-number percentage by which the bar width is scaled down, reserving
   * the remaining space for the value label so it's never pushed out of view.
   */
  barScalePercentage?: number;
  /**
   * When set, renders a full-length track behind the bar in this color so
   * the bar reads against a fixed 100% reference instead of the data max.
   */
  trackColor?: string;
};

const MIN_BAR_WIDTH = 1;

export const AnalyticsBar: FC<AnalyticsBarProps> = ({
  color,
  metricName,
  value,
  width,
  errorBar,
  valueVariant = "monospace",
  tooltip,
  barScalePercentage = 80,
  trackColor,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useLayoutEffect(() => {
    setContainerWidth(containerRef.current?.offsetWidth || 0);
  }, [containerRef.current?.offsetWidth]);

  const scaledWidth = (width * barScalePercentage) / 100;
  const barRadius = `0 ${chartBarRadius.sm}px ${chartBarRadius.sm}px 0`;

  // Inside a track the bar is sized relative to the track, which already
  // occupies barScalePercentage of the row — don't scale down twice. Against
  // the track's explicit 100% reference, a genuine 0% bar must read as empty
  // rather than the minimum-width sliver used without a track, and the bar
  // must never spill past the track even if a caller passes unclamped data.
  const barWidth = trackColor
    ? width === 0
      ? 0
      : Math.min(Math.max(width, MIN_BAR_WIDTH), 100)
    : Math.max(scaledWidth, MIN_BAR_WIDTH);

  const bar = (
    <Box
      bg={color}
      borderRadius={barRadius}
      height={trackColor ? "100%" : 4}
      width={`${barWidth}%`}
      minWidth={scaledWidth === 0 ? 0 : "1px"}
      flexShrink={0}
    />
  );

  const BarComponent = trackColor ? (
    <Box
      bg={trackColor}
      borderRadius={barRadius}
      height={4}
      width={`${barScalePercentage}%`}
      flexShrink={0}
    >
      {bar}
    </Box>
  ) : (
    bar
  );

  return (
    <Box ref={containerRef} position="relative" width="100%">
      <Row align="center" gap={2}>
        {tooltip ? (
          <Tooltip
            placement="left"
            message={
              tooltip.type === "custom" ? (
                tooltip.customTooltip
              ) : (
                <ChartTooltip
                  wrapperVariant="minimal"
                  color={color}
                  title={metricName}
                  subtitles={
                    tooltip.defaultTooltipProps?.tooltipText
                      ? [tooltip.defaultTooltipProps?.tooltipText]
                      : undefined
                  }
                  value={
                    tooltip.defaultTooltipProps?.tooltipValue
                      ? [{ value: tooltip.defaultTooltipProps?.tooltipValue }]
                      : undefined
                  }
                />
              )
            }
          >
            {BarComponent}
          </Tooltip>
        ) : (
          BarComponent
        )}
        {!errorBar && (
          <TextWithTooltip
            size="sm"
            fontWeight="medium"
            variant={valueVariant === "default" ? undefined : valueVariant}
          >
            {value}
          </TextWithTooltip>
        )}
      </Row>

      {errorBar && (
        <AnalyticsErrorBar
          errorBar={errorBar}
          barWidth={scaledWidth}
          containerWidth={containerWidth}
        />
      )}
    </Box>
  );
};

const DefaultErrorBarColor = "#000000";

/**
 * Error bar to represent the confidence interval of the main value's bar.
 * This bar is positioned relative to the main bar given the error bar's bounds.
 */
const AnalyticsErrorBar = ({
  errorBar,
  barWidth,
  containerWidth,
}: {
  errorBar: ErrorBarProps;
  barWidth: number;
  containerWidth: number;
}) => {
  // If the value is 0, there's no need to show the error bar (want to avoid dividing by 0)
  if (errorBar.value === 0) {
    return null;
  }

  // Find the position of the lower and upper bounds of the error bar
  const lowerBoundPosition = (errorBar.lowerBound / errorBar.value) * barWidth;
  const upperBoundPosition = (errorBar.upperBound / errorBar.value) * barWidth;

  // Calculate the width of the error bar in pixels from the bounds' positions
  const errorBarPixelWidth =
    containerWidth * ((upperBoundPosition - lowerBoundPosition) / 100);

  const errorBarColor = errorBar.color ?? DefaultErrorBarColor;

  return (
    <Box
      position="absolute"
      top="4px"
      left={`${lowerBoundPosition}%`}
      width={`${upperBoundPosition - lowerBoundPosition}%`}
      height="8px"
    >
      <ErrorBar width={errorBarPixelWidth} color={errorBarColor} />
    </Box>
  );
};
